import './FiltersPanel.less';
import useAppSelector from '../../hooks/useAppSelector';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import useAppDispatch from '../../hooks/useAppDispatch';
import ColorGridPicker from '../input/ColorGridPicker';
import { useTranslation } from '../../hooks/useTranslation';
import { isFilterableFeatureItem } from '../../services/FeatureService';
import DrawerTitle from '../drawer/DrawerTitle';
import DrawerFooter from '../drawer/DrawerFooter';
import Button from '../button/Button';
import {
    areAppliedFiltersEquals,
    toggleCategoryFilter,
    toggleFieldFilter,
    transformToGroupedFieldFilters
} from '../../services/FilterService';
import { AppliedFilters } from '../../models/AppliedFilters';
import FiltersPanelSection from './FiltersPanelSection';
import FilterValueCheckbox from './FilterValueCheckbox';
import DrawerBody from '../drawer/DrawerBody';
import Drawer from '../drawer/Drawer';

const FiltersPanel = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [expandedFieldName, setExpandedFieldName] = useState<string | null>(
        null
    );
    const [appliedFilters, setAppliedFilters] = useState<
        Partial<AppliedFilters>
    >({});
    const { mainFeatureItem, isFilterPanelOpen } = useAppSelector(
        state => state.featureState
    );

    const toggleSelectedFieldFilterValue = useCallback(
        (fieldName: string, fieldValue: string) => {
            if (!mainFeatureItem || !isFilterableFeatureItem(mainFeatureItem)) {
                return;
            }
            const { availableFieldFilters } = mainFeatureItem;
            const newAppliedFieldFilters = toggleFieldFilter(
                availableFieldFilters,
                appliedFilters.appliedFieldFilters ?? [],
                fieldName,
                fieldValue
            );
            setAppliedFilters({
                ...appliedFilters,
                appliedFieldFilters: newAppliedFieldFilters
            });
        },
        [mainFeatureItem, appliedFilters]
    );

    const toggleSelectedCategoryFilterValue = useCallback(
        (categoryValue: string) => {
            if (!mainFeatureItem || !isFilterableFeatureItem(mainFeatureItem)) {
                return;
            }
            const { availableCategoryFilters } = mainFeatureItem;
            const newAppliedCategoryFilters = toggleCategoryFilter(
                availableCategoryFilters,
                appliedFilters.appliedCategoryFilters ?? [],
                categoryValue
            );
            setAppliedFilters({
                ...appliedFilters,
                appliedCategoryFilters: newAppliedCategoryFilters
            });
        },
        [mainFeatureItem, appliedFilters]
    );

    const syncAppliedFilters = useCallback(() => {
        if (!isFilterableFeatureItem(mainFeatureItem)) {
            return;
        }
        setAppliedFilters({
            appliedCategoryFilters: mainFeatureItem.appliedCategoryFilters,
            appliedFieldFilters: mainFeatureItem.appliedFieldFilters,
            appliedMaxPrice: mainFeatureItem.appliedMaxPrice,
            appliedMinPrice: mainFeatureItem.appliedMinPrice,
            appliedSortBy: mainFeatureItem.appliedSortBy
        });
    }, [mainFeatureItem]);

    useEffect(() => {
        syncAppliedFilters();
    }, [syncAppliedFilters]);

    const groupedFieldFilters = useMemo(() => {
        if (!isFilterableFeatureItem(mainFeatureItem)) {
            return [];
        }
        return transformToGroupedFieldFilters(
            mainFeatureItem.availableFieldFilters,
            appliedFilters.appliedFieldFilters ?? []
        ).filter(
            groupedFieldFilter => !groupedFieldFilter.isFeaturedFilterInUI
        );
    }, [mainFeatureItem, appliedFilters]);

    if (!isFilterableFeatureItem(mainFeatureItem)) {
        return null;
    }

    const isVisibleCategoryFilterSection =
        mainFeatureItem.availableCategoryFilters.length > 1;

    return (
        <Drawer
            isOpen={isFilterPanelOpen}
            onBackdropClick={() => {
                syncAppliedFilters();
                dispatch.featureState.setFilterPanelOpen(false);
            }}
            className={`bri-FiltersPanel`}
        >
            <DrawerTitle
                onCloseClick={() => {
                    syncAppliedFilters();
                    dispatch.featureState.setFilterPanelOpen(false);
                }}
            >
                <div className={'bri-DisplayBoldFont'}>
                    {t('FILTERS_TITLE')}
                </div>
            </DrawerTitle>
            <DrawerBody>
                {isVisibleCategoryFilterSection && (
                    <FiltersPanelSection
                        appliedQuantity={
                            appliedFilters?.appliedCategoryFilters?.length || 0
                        }
                        headerText={t('CATEGORY')}
                        isExpanded={expandedFieldName === '_category'}
                        onToggleExpand={() =>
                            setExpandedFieldName(expandedFieldName =>
                                expandedFieldName === '_category'
                                    ? null
                                    : '_category'
                            )
                        }
                    >
                        {mainFeatureItem.availableCategoryFilters.map(cf => (
                            <FilterValueCheckbox
                                key={cf.categoryValue}
                                label={
                                    cf.categoryTranslatedValue ||
                                    cf.categoryValue
                                }
                                isChecked={
                                    !!appliedFilters.appliedCategoryFilters?.some(
                                        f =>
                                            f.categoryValue === cf.categoryValue
                                    )
                                }
                                onToggle={() =>
                                    toggleSelectedCategoryFilterValue(
                                        cf.categoryValue
                                    )
                                }
                            />
                        ))}
                    </FiltersPanelSection>
                )}
                {groupedFieldFilters.map((groupedFieldFilter, index) => (
                    <FiltersPanelSection
                        appliedQuantity={
                            groupedFieldFilter.fieldValues.filter(
                                fv => fv.isSelected
                            ).length
                        }
                        headerText={
                            groupedFieldFilter.fieldTranslatedName ||
                            groupedFieldFilter.fieldName
                        }
                        isExpanded={
                            expandedFieldName === groupedFieldFilter.fieldName
                        }
                        animationDelayMs={
                            (index + (isVisibleCategoryFilterSection ? 1 : 0)) *
                            100
                        }
                        onToggleExpand={() =>
                            setExpandedFieldName(expandedFieldName =>
                                expandedFieldName ===
                                groupedFieldFilter.fieldName
                                    ? null
                                    : groupedFieldFilter.fieldName
                            )
                        }
                    >
                        <div className="bri-FiltersPanel-group-values">
                            {groupedFieldFilter.fieldType === 'string' &&
                                groupedFieldFilter.fieldValues.map(fv => (
                                    <FilterValueCheckbox
                                        key={fv.fieldValue}
                                        label={
                                            fv.fieldTranslatedValue ||
                                            fv.fieldValue
                                        }
                                        isChecked={fv.isSelected}
                                        onToggle={() =>
                                            toggleSelectedFieldFilterValue(
                                                groupedFieldFilter.fieldName,
                                                fv.fieldValue
                                            )
                                        }
                                    />
                                ))}
                            {groupedFieldFilter.fieldType === 'color' && (
                                <ColorGridPicker
                                    availableColors={groupedFieldFilter.fieldValues.map(
                                        fieldValue => ({
                                            color: fieldValue.color,
                                            id: fieldValue.fieldValue,
                                            isSelected: fieldValue.isSelected,
                                            label: fieldValue.fieldTranslatedValue
                                        })
                                    )}
                                    onItemClick={item =>
                                        toggleSelectedFieldFilterValue(
                                            groupedFieldFilter.fieldName,
                                            item.id
                                        )
                                    }
                                />
                            )}
                        </div>
                    </FiltersPanelSection>
                ))}
            </DrawerBody>
            <DrawerFooter>
                <Button
                    disabled={
                        mainFeatureItem.refinementLoadingStatus === 'loading'
                    }
                    variant="tertiary"
                    fullWidth
                    size="medium"
                    onClick={() => {
                        dispatch.featureState.setFilterPanelOpen(false);
                        void dispatch.featureState.applyProductSearchFilters({
                            appliedCategoryFilters: [],
                            appliedFieldFilters: [],
                            appliedMaxPrice: null,
                            appliedMinPrice: null
                        });
                    }}
                >
                    {t('CLEAR_FILTERS')}
                </Button>
                <Button
                    disabled={
                        mainFeatureItem.refinementLoadingStatus === 'loading'
                    }
                    variant="primary"
                    fullWidth
                    size="medium"
                    onClick={() => {
                        dispatch.featureState.setFilterPanelOpen(false);
                        if (
                            !areAppliedFiltersEquals(
                                mainFeatureItem,
                                appliedFilters
                            )
                        ) {
                            void dispatch.featureState.applyProductSearchFilters(
                                appliedFilters
                            );
                        }
                    }}
                >
                    {t('VIEW_FILTERS_RESULT')}
                </Button>
            </DrawerFooter>
        </Drawer>
    );
};

export default FiltersPanel;
